import type { PermissionRequest, QuestionRequest } from "@mux/sdk/v2"
import type { ServerConnection } from "@/context/server"
import { jsonArray, jsonObject, jsonString } from "./decode"
import { expectJson } from "./http"
import { serverFetch } from "./server"

/**
 * Where a chat's open questions and permission asks surface: the chat itself,
 * then every chat its open work reports to, nearest first. The server decides
 * it from stored delegations.
 */
export type RequestRoute = { sessionID: string; workspaceID: string; title: string; surfacesIn: string[] }
/** The chat a question or permission ask came from. */
export type RequestAsker = Pick<RequestRoute, "sessionID" | "title" | "workspaceID">
/** Every open question and permission ask on a machine, with each asking chat's route. */
export type OpenRequests = { questions: QuestionRequest[]; permissions: PermissionRequest[]; routes: RequestRoute[] }

export const REQUEST_ROUTE_EVENT = "request.route"

const routeError = "Server returned an invalid request route"

/**
 * The route an asked event's request carries: where it surfaces as it
 * arrives. Requests from a snapshot carry none; the snapshot lists routes.
 */
export function askedRequestRoute(request: object): RequestRoute | undefined {
  return "route" in request && request.route !== undefined ? decodeRequestRoute(request.route) : undefined
}

export function decodeRequestRoute(value: unknown): RequestRoute {
  const route = jsonObject(value, routeError)
  const sessionID = jsonString(route.sessionID, routeError)
  const surfacesIn = jsonArray(route.surfacesIn, routeError).map((id) => jsonString(id, routeError))
  if (surfacesIn[0] !== sessionID) throw new Error(routeError)
  return {
    sessionID,
    workspaceID: jsonString(route.workspaceID, routeError),
    title: jsonString(route.title, routeError),
    surfacesIn,
  }
}

function decodeOpenRequests(value: unknown): OpenRequests {
  const error = "Server returned invalid open requests"
  const body = jsonObject(value, error)
  const request = (item: unknown) => {
    const row = jsonObject(item, error)
    jsonString(row.id, error)
    jsonString(row.sessionID, error)
    return row
  }
  return {
    questions: jsonArray(body.questions, error).map((item) => {
      const row = request(item)
      jsonArray(row.questions, error)
      return row as QuestionRequest
    }),
    permissions: jsonArray(body.permissions, error).map((item) => {
      const row = request(item)
      jsonString(row.permission, error)
      jsonArray(row.patterns, error)
      return row as PermissionRequest
    }),
    routes: jsonArray(body.routes, error).map(decodeRequestRoute),
  }
}

export async function getOpenRequests(server: ServerConnection.HttpBase, signal?: AbortSignal) {
  return expectJson(await serverFetch(server, "/session/requests", { signal }), decodeOpenRequests)
}
